/**
 * Furniture / build tiers on every quote (inactive until BLISS_FEATURES.threeTierQuotes.active).
 */

import type { BlissCollectionId } from './blissCollections'

export type BlissBuildTierId = 'essential' | 'reserve' | 'signature' | 'estate'

export interface BlissBuildTier {
  id: BlissBuildTierId
  label: string
  subtitle: string
  materialsSummary: string
  /** Multiplier vs Reserve baseline (1.0). Essential < 1, Signature/Estate > 1. */
  priceMultiplier: number
  linenRefreshMonths: 12 | 6 | 3
  craftsmanshipYears: number
  /** Included hospitality kit cadence (see blissHospitality). */
  defaultKitProgram:
    | 'refresh_single_1'
    | 'refresh_bunk_2'
    | 'bliss_box_club_annual'
    | 'estate_hospitality_bundle'
  highlights: string[]
}

export const BLISS_BUILD_TIERS: BlissBuildTier[] = [
  {
    id: 'essential',
    label: 'Bliss Essential',
    subtitle: 'Smart custom build',
    materialsSummary: 'Birch hardwood ply frame & platforms',
    priceMultiplier: 0.86,
    linenRefreshMonths: 12,
    craftsmanshipYears: 1,
    defaultKitProgram: 'refresh_single_1',
    highlights: ['Annual linen refresh (per enrolled room)', 'Standard install scheduling'],
  },
  {
    id: 'reserve',
    label: 'Bliss Reserve',
    subtitle: 'Most chosen',
    materialsSummary: 'Maple hardwood ply frame & platforms',
    priceMultiplier: 1,
    linenRefreshMonths: 6,
    craftsmanshipYears: 2,
    defaultKitProgram: 'refresh_bunk_2',
    highlights: ['Linen refresh every 6 months', 'Priority fabrication queue'],
  },
  {
    id: 'signature',
    label: 'Bliss Signature',
    subtitle: 'Solid hardwood presence',
    materialsSummary: 'Solid white maple hardwood (key faces & structure)',
    priceMultiplier: 1.32,
    linenRefreshMonths: 3,
    craftsmanshipYears: 3,
    defaultKitProgram: 'bliss_box_club_annual',
    highlights: ['Quarterly linen refresh', 'Extended design presentation'],
  },
  {
    id: 'estate',
    label: 'Bliss Estate',
    subtitle: 'Hospitality at home',
    materialsSummary: 'Signature build + full hospitality program',
    priceMultiplier: 2.4,
    linenRefreshMonths: 3,
    craftsmanshipYears: 5,
    defaultKitProgram: 'estate_hospitality_bundle',
    highlights: [
      'Bliss Box quarterly (per enrolled room)',
      'Hospitality membership included',
      'Multi-room portfolio planning',
    ],
  },
]

export function tierById(id: BlissBuildTierId): BlissBuildTier {
  const t = BLISS_BUILD_TIERS.find((x) => x.id === id)
  if (!t) throw new Error(`Unknown tier: ${id}`)
  return t
}

/** Quote line helper — not applied to live pricing until tier quotes are active. */
export function tierAdjustedTotal(reserveBaselineTotal: number, tierId: BlissBuildTierId): number {
  return Math.round(reserveBaselineTotal * tierById(tierId).priceMultiplier)
}

export function depositDueNow(
  projectTotal: number,
  consultPaid: boolean,
  depositPercent = 0.5,
  consultCredit = 149,
): number {
  const gross = Math.round(projectTotal * depositPercent * 100) / 100
  return Math.max(0, gross - (consultPaid ? consultCredit : 0))
}

export type BlissQuoteDraft = {
  reserveBaselineTotal: number
  selectedTierId: BlissBuildTierId
  collectionId?: BlissCollectionId
  consultPaid: boolean
  rooms: import('../types/blissPrograms').BlissRoomEnrollment[]
}

export function summarizeQuoteDraft(draft: BlissQuoteDraft) {
  const total = tierAdjustedTotal(draft.reserveBaselineTotal, draft.selectedTierId)
  const deposit = depositDueNow(total, draft.consultPaid)
  const grossDeposit = Math.round(total * 50) / 100
  return { total, deposit, balanceBeforeDelivery: total - grossDeposit }
}
